import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Input } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule, MatSuffix } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import {
  IApnNewSpaceEmailLink,
  ICurrentLotParkingAgreementVerbiage,
  IParkerProfileDetail,
  IParkingAgreementFetchDetail,
  IParkingAgreementNewParkingAgreementLink,
  IParkingAgreementUpdateExemptStatus,
  ISpaceEmailByEntityId,
} from 'src/app/shared/interfaces';
import { DialogConfirmSpaceEmailComponent } from '../../parker-v2-dialogs/dialog-confirm-space-email/dialog-confirm-space-email.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorDialogStopComponent } from 'src/app/core/dialog/error-dialog-stop/error-dialog-stop.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DialogUpdateParkerEmailaddressComponent } from '../../parker-v2-dialogs/dialog-update-parker-emailaddress/dialog-update-parker-emailaddress.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { environment } from 'src/environments/environment';
import { check } from '@igniteui/material-icons-extended';

export function validateNoSpaces(control: AbstractControl) {
  try {
    const controlValue: string = control.value.toString();
    const checkEmptyString = controlValue.trim();

    if (checkEmptyString.length > 0) {
      // input is valid
      return null;
    }
  } catch (err) {
    return null;
  }

  return {
    validateNoSpaces: false,
  };
}

@Component({
  selector: 'app-parkers-v2-agreement',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    MatCheckboxModule,
    MatSnackBarModule,
    MatIconModule,
    MatTooltipModule,
    MatSuffix,
    MatButtonModule,
    ClipboardModule,
    DatePipe,
  ],
  templateUrl: './parkers-v2-agreement.component.html',
  styleUrl: './parkers-v2-agreement.component.scss',
})
export class ParkersV2AgreementComponent implements OnInit {
  status_agreementNotSentOrExempt = -1;
  status_agreementEmailSent: number = 0;
  status_agreementExempt: number = 1;
  status_agreementRemovedFromExemptAndEmailSent: number = 2;
  status_agreementExpired: number = 3;
  status_agreementParkerTermed: number = 4;
  status_agreementViewedButNotSigned: number = 5;
  status_agreementSigned: number = 6;
  status_agreementResent: number = 7;
  status_agreementNotificationFailed: number = 8;
  status_agreementExemptRemoved: number = 9;

  @Input() parkerDetail: IParkerProfileDetail;
  spaceEmailValue: string = '';
  parkerId: string;
  resendEmailMode: boolean = false;
  hasAgreementBeenSigned: boolean = true;
  isExempt: boolean = false;
  nameOfSignedParker: string = 'Ken Moss';
  currentParkingAgreement: IParkingAgreementFetchDetail;
  parkingAgreementCopyUrl: string = 'No Current Parking Agreement';
  parkingAgreementExists: boolean = false;
  currentParkingAgreementStatus: number = -1;
  lotParkingAgreementSettings: ICurrentLotParkingAgreementVerbiage;
  localDateTime: Date;
  sendInviteButtonText: string = 'Send Agreement Notification';
  isDevEnvironment: boolean = false;
  dateResentVerbiage: string = 'None';
  isExemptValidToSubmit: boolean = false;

  parkerNotificationForm = new FormGroup({
    emailAddress: new FormControl('', [Validators.required]),
    dateLastModified: new FormControl('', []),
    dateEmailResent: new FormControl('', []),
    linkInviteForm: new FormControl(false, []),
    includeSpecialInstructions: new FormControl(false, []),
  });

  agreementExemptionForm = new FormGroup({
    isExempt: new FormControl(false, [Validators.required]),
    exemptComments: new FormControl('', [Validators.required, validateNoSpaces]),
  });

  private destroyRefService = inject(DestroyRef);
  private appGeneralState = inject(AppGeneralStateService);

  constructor(
    private dataService: DataService,
    private parkerStateService: ParkerStateService,
    private snackBarService: MatSnackBar,
    private authService: AuthService,
    private datePipe: DatePipe,
    public dialog: MatDialog,
  ) {
    if (environment.UrlDomain == 'dev.acepark.net') this.isDevEnvironment = true;
    else this.isDevEnvironment = false;
  }

  ngOnInit(): void {
    this.getLotSpecificParkingAgreement(this.parkerDetail.lotNumber);
  }

  // evaluateCompanyOrParkerPaidType(): void {
  //   if (this.parkerDetail.parkerPayType === 'Company') {
  //     this.isCompanyPaid = true;
  //   } else {
  //     this.isCompanyPaid = false;
  //   }
  // }

  public onKeyDown(keyDown: KeyboardEvent): void {
    if (keyDown.code == 'Space') keyDown.stopPropagation();
  }

  onSubmitExemptAgreement(): void {
    this.updateExemptStatus();
  }

  onResetParkingAgreementTable(): void {
    this.dataService
      .resetParkingAgreementTable()
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => {},
        error: (err) => {},
        complete: () => {
          this.snackBarService.open('The Parking Agreement table has been reset', '', { duration: 4000 });
        },
      });
  }

  setParkerNotificationFormValues(): void {
    if (this.parkingAgreementExists) {
      //Set Lease Exemption Values
      this.agreementExemptionForm.setValue({
        isExempt: this.currentParkingAgreement.isExemptMonthlyAgreement,
        exemptComments: this.currentParkingAgreement.exemptMonthlyAgreementComments,
      });

      this.agreementExemptionForm.updateValueAndValidity();

      const originalInviteDate: string = new DatePipe('en-US').transform(
        this.currentParkingAgreement.originalNotificationDateSent,
        'M/d/yyyy',
      );

      const dateResent: string = new DatePipe('en-US').transform(
        this.currentParkingAgreement.lastDateNotificationResend,
        'M/d/yyyy',
      );

      if (originalInviteDate != '12/31/0001' && originalInviteDate != '1/1/0001') {
        this.parkerNotificationForm.patchValue({
          dateLastModified: new DatePipe('en-US').transform(
            this.currentParkingAgreement.originalNotificationDateSent,
            'M/d/yyyy',
          ),
        });
      }

      if (dateResent != '12/31/0001' && dateResent != '1/1/0001') {
        this.parkerNotificationForm.patchValue({
          dateEmailResent: new DatePipe('en-US').transform(
            this.currentParkingAgreement.lastDateNotificationResend,
            'M/d/yyyy',
          ),
        });
      }

      if (
        this.currentParkingAgreement.agreementStatus == this.status_agreementEmailSent ||
        this.currentParkingAgreement.agreementStatus == this.status_agreementViewedButNotSigned
      ) {
        this.sendInviteButtonText = 'Resend Agreement Notifications';
      }

      if (
        this.currentParkingAgreement.agreementStatus == this.status_agreementNotSentOrExempt ||
        this.currentParkingAgreement.agreementStatus == this.status_agreementExempt
      ) {
        this.sendInviteButtonText = 'Send Agreement Notifications';
      }

      if (this.currentParkingAgreement.isExemptMonthlyAgreement) {
        this.parkerNotificationForm.patchValue({
          linkInviteForm: this.currentParkingAgreement.includeDataRequest,
          includeSpecialInstructions: this.currentParkingAgreement.includeSpecialInstructions,
          emailAddress: this.parkerDetail.emailAddress,
        });
      } else {
        this.parkerNotificationForm.patchValue({
          linkInviteForm: this.currentParkingAgreement.includeDataRequest,
          includeSpecialInstructions: this.currentParkingAgreement.includeSpecialInstructions,
          emailAddress: this.currentParkingAgreement.recipientEmailAddress,
        });
      }
    } else {
      if (this.lotParkingAgreementSettings.defaultValueSendParkingAgreement == false) {
        this.parkerNotificationForm.patchValue({
          linkInviteForm: false,
          includeSpecialInstructions: false,
          emailAddress: this.parkerDetail.emailAddress,
        });
      } else {
        this.parkerNotificationForm.patchValue({
          linkInviteForm: this.lotParkingAgreementSettings.defaultValueSendParkingAgreement,
          includeSpecialInstructions: this.lotParkingAgreementSettings.includeSpecialInstructions,
          emailAddress: this.parkerDetail.emailAddress,
        });
      }
    }
  }

  onValidateAgreementExempt(): void {
    const isExempt: boolean = this.agreementExemptionForm.get('isExempt').value;
    const exemptComments: string = this.agreementExemptionForm.get('exemptComments').value;

    if (isExempt) {
      this.isExemptValidToSubmit = exemptComments != '';
    } else {
      this.isExemptValidToSubmit = true;
    }
  }

  confirmUpdateEmailAddress(): void {
    const enterAnimationDuration: string = '0ms';
    const exitAnimationDuration: string = '0ms';
    const existingEmailAddress: string = this.parkerNotificationForm.get('emailAddress').value;

    const dialogRef = this.dialog.open(DialogUpdateParkerEmailaddressComponent, {
      data: { parkerId: this.parkerDetail.parkerId, emailAddress: existingEmailAddress },
      width: '550px',
      enterAnimationDuration,
      exitAnimationDuration,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result != false) {
        this.onUpdateNewParkerEmailAfterUpdate(result);
        this.snackBarService.open('The Email Address for this Parker has been successfully updated.', '', {
          duration: 5000,
        });
      }
    });
  }

  onUpdateNewParkerEmailAfterUpdate(newEmailAddress: string): void {
    this.parkerNotificationForm.patchValue({
      emailAddress: newEmailAddress,
    });

    //Update data in Parker State
    const index: number = this.parkerStateService.parkerQueryResults.findIndex(
      (query) => query.parkerId == this.parkerDetail.parkerId,
    );
    this.parkerStateService.parkerQueryResults[index].emailAddress = newEmailAddress;

    const indexForFilteredQuery: number = this.parkerStateService.filteredParkers.findIndex(
      (query) => query.parkerId == this.parkerDetail.parkerId,
    );
    this.parkerStateService.filteredParkers[indexForFilteredQuery].emailAddress = newEmailAddress;
  }

  getParkingAgreementDataForParker(parkerId: string): void {
    this.dataService
      .getParkingAgreementDetailByParkerId(parkerId)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => {
          this.currentParkingAgreement = results;
          this.setParkingAgreementLink();
          this.parkingAgreementExists = true;
        },
        error: (err) => {
          this.parkingAgreementExists = false;
          this.setParkerNotificationFormValues();
        },
        complete: () => {
          this.currentParkingAgreementStatus = this.currentParkingAgreement.agreementStatus;
          this.setParkerNotificationFormValues();
          this.setLocalDateTime();
          this.setDateResentVerbiage();
          this.onValidateAgreementExempt();
        },
      });
  }

  setLocalDateTime(): void {
    const dateTimeString: string = this.currentParkingAgreement.dateAgreementAcknowledged.toString();
    const timezoneOffsetInMinutes = new Date().getTimezoneOffset();
    const timezoneOffsetInHours = (timezoneOffsetInMinutes / 60) * -1;

    if (dateTimeString === '0001-01-01T00:00:00+00:00') {
    } else {
      this.localDateTime = this.addHoursToDate(
        this.currentParkingAgreement.dateAgreementAcknowledged,
        timezoneOffsetInHours,
      );
    }
  }

  setDateResentVerbiage(): void {
    const resentDateTime: string = this.currentParkingAgreement.lastDateNotificationResend.toString();

    if (resentDateTime == '0001-01-01T00:00:00+00:00') {
      this.dateResentVerbiage = 'None';
    } else {
      this.dateResentVerbiage = this.datePipe.transform(
        this.currentParkingAgreement.lastDateNotificationResend,
        'short',
      );
    }
  }

  addHoursToDate(date, hours): Date {
    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + hours);
    return newDate;
  }

  setExemptStatusModel(): IParkingAgreementUpdateExemptStatus {
    let exemptData: IParkingAgreementUpdateExemptStatus = {} as IParkingAgreementUpdateExemptStatus;

    exemptData.lotNumber = this.parkerDetail.lotNumber;
    exemptData.parkerId = this.parkerDetail.parkerId;
    try {
      exemptData.exemptStatus = this.agreementExemptionForm.get('isExempt').value!;
    } catch (err) {
      exemptData.exemptStatus = false;
    }

    if (exemptData.exemptStatus == true) {
      exemptData.agreementStatus = 1; //this is the status code for designating a parker as exempt from the parking agreement
    } else {
      exemptData.agreementStatus = 9; //status for removing a Parker from exempt status on Parking Agreement
    }

    exemptData.userNameLastUpdate = this.appGeneralState.userName;

    //if the exempt status is being set to true, then we include the comments.
    //If the exempt status is being removed, then set the comments to empty string
    exemptData.exemptComments = exemptData.exemptStatus ? this.agreementExemptionForm.get('exemptComments').value! : '';

    return exemptData;
  }

  sendSignedParkerAgreement(): void {
    const parkerId: string = this.currentParkingAgreement.parkerId;

    this.showStaticSnackBarMessage(`Stand by while we send a copy of the Monthly Parking Agreement...`);

    this.dataService
      .parkingAgreementResendExecutedContract(parkerId)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => {},
        error: (err) => {
          this.closeStaticSnackBar();
        },
        complete: () => {
          this.closeStaticSnackBar();
          let verticalPosition: MatSnackBarVerticalPosition = 'top';

          this.snackBarService.open('The Parking Agreement has been sent to the Parker', '', {
            duration: 4000,
            verticalPosition: verticalPosition,
          });
        },
      });
  }

  updateExemptStatus(): void {
    const exemptStatusData: IParkingAgreementUpdateExemptStatus = this.setExemptStatusModel();
    let confirmationMessage: string = '';

    if (exemptStatusData.exemptStatus) {
      confirmationMessage = 'This Parker has been successfully designated as exempt.';
    } else {
      confirmationMessage = 'This Parker is no longer exempt from the Parking Agreement.';
    }

    this.dataService
      .updateParkingAgreementExemptStatus(exemptStatusData)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => {},
        error: (err) => {},
        complete: () => {
          let verticalPosition: MatSnackBarVerticalPosition = 'top';

          this.snackBarService.open(confirmationMessage, '', {
            duration: 4000,
            verticalPosition: verticalPosition,
          });

          //if the exemptioin is being removed, then clear out any exemption reason comments that might have existed
          if (exemptStatusData.exemptStatus == false) {
            this.agreementExemptionForm.patchValue({
              exemptComments: '',
            });
          }
        },
      });
  }

  getLotSpecificParkingAgreement(lotNumber: string): void {
    this.dataService
      .GetParkingAgreementVerbiageForLot(lotNumber)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => {
          this.lotParkingAgreementSettings = results;
        },
        error: (err) => {},
        complete: () => {
          if (this.lotParkingAgreementSettings.defaultValueSendParkingAgreement == false) {
            this.parkerNotificationForm.patchValue({
              linkInviteForm: false,
              includeSpecialInstructions: false,
              emailAddress: this.parkerDetail.emailAddress,
            });
          } else {
            this.parkerNotificationForm.patchValue({
              linkInviteForm: this.lotParkingAgreementSettings.defaultValueSendParkingAgreement,
              includeSpecialInstructions: this.lotParkingAgreementSettings.includeSpecialInstructions,
              emailAddress: this.parkerDetail.emailAddress,
            });
          }

          this.getParkingAgreementDataForParker(this.parkerDetail.parkerId);
        },
      });
  }

  onSubmitSendNotification(): void {
    const agreementData: IParkingAgreementNewParkingAgreementLink = this.createParkingAgreementModel();

    if (this.currentParkingAgreement == undefined) {
      agreementData.isResendNotification = false;
    } else {
      if (
        this.currentParkingAgreement.agreementStatus == this.status_agreementNotSentOrExempt ||
        this.currentParkingAgreement.agreementStatus == this.status_agreementExempt
      ) {
        agreementData.isResendNotification = false;
      } else {
        agreementData.isResendNotification = true;
      }
    }
    this.onSendNewParkingAgreement(agreementData);
    this.onValidateAgreementExempt();
  }

  createParkingAgreementModel(): IParkingAgreementNewParkingAgreementLink {
    let agreementData: IParkingAgreementNewParkingAgreementLink = {} as IParkingAgreementNewParkingAgreementLink;

    agreementData.parkerId = this.parkerDetail.parkerId;
    agreementData.lotNumber = this.parkerDetail.lotNumber;
    agreementData.userName = this.appGeneralState.userName;
    agreementData.recipientEmailAddress = this.parkerNotificationForm.get('emailAddress')!.value!;
    agreementData.isExempt = false;

    try {
      agreementData.includeDataRequest = this.parkerNotificationForm.get('linkInviteForm')!.value!;
    } catch (err) {
      agreementData.includeDataRequest = false;
    }

    try {
      agreementData.includeSpecialInstructions = this.parkerNotificationForm.get('includeSpecialInstructions')!.value!;
    } catch (err) {
      agreementData.includeSpecialInstructions = false;
    }

    return agreementData;
  }

  //This creates/updates a new Parking Agreement row and sends an email notification to the Parker
  onSendNewParkingAgreement(agreementData: IParkingAgreementNewParkingAgreementLink): void {
    this.showStaticSnackBarMessage(
      `Stand by while we set up the Parking Agreement and send an email to Parker: ${agreementData.recipientEmailAddress}`,
    );
    this.dataService
      .sendParkingAgreementNotification(agreementData)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => {},
        error: (err) => {
          this.closeStaticSnackBar();
        },
        complete: () => {
          this.closeStaticSnackBar();
          this.getParkingAgreementDataForParker(this.parkerDetail.parkerId);
        },
      });
  }

  onSendParkerSignedAgreement(): void {
    this.sendSignedParkerAgreement();
  }

  onCopyMessage(message: string): void {
    this.snackBarService.open(message, '', { duration: 3000 });
  }

  //This sets the URL to access the Parking Agreement for a specific Parker
  setParkingAgreementLink(): void {
    this.parkingAgreementCopyUrl = `${environment.SpaceArPublic}#/reservationlink/${this.currentParkingAgreement.agreementLink}`;
    // 'http://localhost:2222/#/reservationlink/8BBD3E1C-BA07-42F6-801F-8F5315CE0CA1';
  }

  showStaticSnackBarMessage(message: string): void {
    let verticalPosition: MatSnackBarVerticalPosition = 'top';

    this.snackBarService.open(message, '', { verticalPosition: verticalPosition });
  }

  closeStaticSnackBar(): void {
    this.snackBarService.dismiss();
  }
}
